/**
 * Enums canonicos da API — transcricao literal de `docs/CONTRACTS.md §2`.
 *
 * Regra de ouro deste arquivo: **grafia exata do documento**. Um valor a mais, um a menos
 * ou um acento fora do lugar e uma divergencia de contrato, e a reconciliacao gastou cinco
 * rodadas fechando exatamente esse tipo de divergencia. Por isso cada enum vem acompanhado
 * de uma constante `*_VALUES` (a lista) e de um teste de **cardinalidade** em `enums.test.ts`
 * que quebra se alguem acrescentar valor sem atualizar o documento.
 *
 * Sobre o subconjunto de MVP: CONTRACTS §2 marca cada valor com **M** (MVP contratavel, D8)
 * ou **P** (previsto — existe no enum e nos contratos previstos, mas a aplicacao rejeita/oculta).
 * As listas `*_MVP_VALUES` sao esse recorte, para que o gate de fail-closed tenha de onde ler.
 *
 * Sobre os enums compartilhados com `@farmacia/domain`: **eles nao sao redigitados aqui.**
 * CONTRACTS §2.7 e explicito — "Um unico enum, em `packages/domain`" — e a mesma regra vale
 * para `RegulatoryClass` (§2.6, ADR-0011), `OrderStatus` (§2.1) e `PromotionType` (§2.8).
 * Este arquivo **reexporta a lista do dominio** e apenas a embrulha em `z.enum(...)`, que e o
 * que `contracts` acrescenta: validacao de wire e geracao de OpenAPI. Duas listas do mesmo
 * enum e exatamente o defeito que X-34, X-01 e D6 fecharam, e reexportar torna a divergencia
 * **impossivel** em vez de meramente testada. `enums.domain-conformance.test.ts` prova por
 * identidade de referencia que a derivacao continua de pe.
 *
 * Nota de precedencia: `API.md §10.3` afirma o contrario ("`packages/contracts` nao importa
 * nada de `packages/domain`"). Perde. A ordem de precedencia da Fase 2 e
 * `adr/` + `CONTRACTS.md` > `IMPLANTACAO.md` > `DOMAIN.md` > `DATA_MODEL.md` > `API.md`, e o
 * gate executavel `contracts-so-depende-de-domain` do `.dependency-cruiser.cjs` ja autoriza
 * este import nominalmente. A divergencia esta reportada para correcao de `API.md`.
 */
import {
  ACTOR_KINDS,
  ADJUSTMENT_TIMEOUT_DEFAULTS,
  CANCELLATION_REASONS,
  DEFAULT_SELLABLE_CLASSES,
  FULFILLMENT_MODES,
  INITIAL_ORDER_STATUSES,
  MVP_OFFER_MECHANICS,
  MVP_ORDER_STATUSES,
  MVP_STAFF_ROLES,
  ORDER_CHANNELS,
  ORDER_STATUSES,
  PROFILE_FORBIDDEN_CLASSES,
  PROMOTION_TYPES,
  REGULATORY_CLASSES,
  STAFF_ROLES,
  TERMINAL_ORDER_STATUSES,
} from '@farmacia/domain';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// §2.1 OrderStatus — ciclo de fulfillment do pedido (D3; DOMAIN §4.2 soberano)
// ---------------------------------------------------------------------------

/**
 * 12 estados de fulfillment. Devolucao vive em `ReturnRequest.status`, reembolso em
 * `Refund.status` e o estado financeiro em `PaymentStatus` — por isso `REEMBOLSADO`,
 * `EM_DEVOLUCAO`, `DEVOLVIDO`, `PAGAMENTO_EXPIRADO` e `EM_CONFERENCIA` **nunca** existirao
 * aqui (X-01).
 */
export const ORDER_STATUS_VALUES = ORDER_STATUSES;

export const OrderStatusSchema = z.enum(ORDER_STATUS_VALUES);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

/** Os 8 estados do MVP (CONTRACTS §2.1, coluna M). */
export const ORDER_STATUS_MVP_VALUES = MVP_ORDER_STATUSES;

export const OrderStatusMvpSchema = z.enum(ORDER_STATUS_MVP_VALUES);
export type OrderStatusMvp = z.infer<typeof OrderStatusMvpSchema>;

/**
 * Estados iniciais possiveis (CONTRACTS §2.1). **Sem `@default` na coluna** (X-42): o estado
 * inicial e sempre explicito na criacao.
 */
export const ORDER_STATUS_INITIAL_VALUES = INITIAL_ORDER_STATUSES;

/** Terminais de fulfillment (CONTRACTS §2.1). */
export const ORDER_STATUS_TERMINAL_VALUES = TERMINAL_ORDER_STATUSES;

/** Obrigatorio em `CANCELADO` — lista fechada (CONTRACTS §2.1, DOMAIN §4.2). */
export const CANCELLATION_REASON_VALUES = CANCELLATION_REASONS;

export const CancellationReasonSchema = z.enum(CANCELLATION_REASON_VALUES);
export type CancellationReason = z.infer<typeof CancellationReasonSchema>;

/**
 * Modo de atendimento do pedido. No MVP `ENTREGUE` cobre tambem a retirada concluida e e este
 * campo que distingue os dois nos relatorios (CONTRACTS §2.1).
 */
export const FULFILLMENT_MODE_VALUES = FULFILLMENT_MODES;
export const FulfillmentModeSchema = z.enum(FULFILLMENT_MODE_VALUES);
export type FulfillmentMode = z.infer<typeof FulfillmentModeSchema>;

// ---------------------------------------------------------------------------
// §2.2 ReturnRequestStatus e RefundStatus (D3)
// ---------------------------------------------------------------------------

/**
 * Maquina do DOMAIN §4.2.4. O agregado inteiro e **P**: no MVP a devolucao e atendida
 * manualmente e o estorno registrado em `Refund`.
 */
export const RETURN_REQUEST_STATUS_VALUES = [
  'SOLICITADA',
  'APROVADA',
  'RECUSADA',
  'COLETA_AGENDADA',
  'RECEBIDA',
  'DESTINADA', // `disposition` derivada da classe, nunca escolhida (REG-060)
  'REEMBOLSADA',
] as const;

export const ReturnRequestStatusSchema = z.enum(RETURN_REQUEST_STATUS_VALUES);
export type ReturnRequestStatus = z.infer<typeof ReturnRequestStatusSchema>;

/**
 * Destino do item devolvido — **derivado da classe regulatoria, nunca escolhido** (REG-060):
 * medicamento/NBCAL vao para descarte sanitario (RDC 222/2018); os demais retornam ao estoque.
 */
export const RETURN_DISPOSITION_VALUES = ['SANITARY_DISPOSAL', 'RESTOCK'] as const;
export const ReturnDispositionSchema = z.enum(RETURN_DISPOSITION_VALUES);
export type ReturnDisposition = z.infer<typeof ReturnDispositionSchema>;

/** **M** — o estorno manual do MVP tambem e registrado aqui (CONTRACTS §2.2). */
export const REFUND_STATUS_VALUES = [
  'SOLICITADO',
  'APROVACAO_PENDENTE', // acima do limite diario da filial (§10) — aguarda STORE_MANAGER
  'ENVIADO',
  'LIQUIDADO',
  'FALHOU',
] as const;

export const RefundStatusSchema = z.enum(REFUND_STATUS_VALUES);
export type RefundStatus = z.infer<typeof RefundStatusSchema>;

/** `RefundReason` (DATA_MODEL, mantido em CONTRACTS §2.2). */
export const REFUND_REASON_VALUES = [
  'ARREPENDIMENTO_CDC_49',
  'RUPTURA',
  'CANCELAMENTO_LOJA',
  'CANCELAMENTO_CLIENTE',
  'NAO_ENTREGUE',
  'ERRO_OPERACIONAL',
  'SLA_ACEITE',
] as const;

export const RefundReasonSchema = z.enum(REFUND_REASON_VALUES);
export type RefundReason = z.infer<typeof RefundReasonSchema>;

// ---------------------------------------------------------------------------
// §2.3 PaymentMethod (S-04 / D-10)
// ---------------------------------------------------------------------------

/**
 * Grafia canonica em **portugues**. Os nomes em ingles que circulavam na API
 * (`CASH_ON_DELIVERY`, `CARD_ON_DELIVERY`, `CARD_ONLINE`) estao **eliminados**.
 */
export const PAYMENT_METHOD_VALUES = [
  'PIX', // M
  'DINHEIRO_ENTREGA', // M — com `changeForCents`; sujeito ao limite de cliente novo (§10)
  'MAQUININHA_ENTREGA', // M
  'CREDIARIO', // P — atras de FEATURE_STORE_CREDIT, default off em toda instancia
  'CARTAO_ONLINE', // P
] as const;

export const PaymentMethodSchema = z.enum(PAYMENT_METHOD_VALUES);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const PAYMENT_METHOD_MVP_VALUES = [
  'PIX',
  'DINHEIRO_ENTREGA',
  'MAQUININHA_ENTREGA',
] as const satisfies readonly PaymentMethod[];

// ---------------------------------------------------------------------------
// §2.4 PaymentStatus
// ---------------------------------------------------------------------------

export const PAYMENT_STATUS_VALUES = [
  'PENDENTE',
  'PAGO', // confirmado por RECONSULTA ao PSP (webhook nunca e fonte) ou baixa do COD
  'EXPIRADO', // estado da COBRANCA, nao do pedido
  'CANCELADO',
  'ESTORNADO_PARCIAL',
  'ESTORNADO_TOTAL',
] as const;

export const PaymentStatusSchema = z.enum(PAYMENT_STATUS_VALUES);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

// ---------------------------------------------------------------------------
// §2.5 OrderChannel (D9 — pedido assistido e primeira classe)
// ---------------------------------------------------------------------------

export const ORDER_CHANNEL_VALUES = ORDER_CHANNELS;

export const OrderChannelSchema = z.enum(ORDER_CHANNEL_VALUES);
export type OrderChannel = z.infer<typeof OrderChannelSchema>;

export const ORDER_CHANNEL_MVP_VALUES = [
  'PWA',
  'WHATSAPP_ASSISTIDO',
] as const satisfies readonly OrderChannel[];

// ---------------------------------------------------------------------------
// §2.6 RegulatoryClass — contagem canonica: 16 (X-34 / D-10)
// ---------------------------------------------------------------------------

/**
 * As contagens "15" e "11" que circulavam em outros documentos estavam erradas.
 * Sao **16** valores, e este numero e contratual: a matriz de controles e 16 x 7 = 112
 * asseroes (REG-011, ADR-0011) e a matriz oferta x classe e 10 x 16 = 160 celulas (§9).
 *
 * O enum e contrato do produto; o subconjunto **vendavel** e perfil de instancia
 * (`regulatory.sellableClasses`, CONTRACTS §1.3).
 */
export const REGULATORY_CLASS_VALUES = REGULATORY_CLASSES;

export const RegulatoryClassSchema = z.enum(REGULATORY_CLASS_VALUES);
export type RegulatoryClass = z.infer<typeof RegulatoryClassSchema>;

/**
 * Default de `regulatory.sellableClasses` (CONTRACTS §2.6). Nao e o enum: e o recorte que
 * uma instancia recem-provisionada vende antes de qualquer decisao do implantador.
 */
export const REGULATORY_CLASS_DEFAULT_SELLABLE_VALUES = DEFAULT_SELLABLE_CLASSES;

/**
 * Classes **rejeitadas pelo schema do perfil no boot** — nao ha gate, flag nem combinacao que
 * as torne visiveis (CONTRACTS §2.6). E a unica decisao em que o produto nao confia no
 * implantador.
 */
export const REGULATORY_CLASS_NEVER_SELLABLE_VALUES = PROFILE_FORBIDDEN_CLASSES;

// ---------------------------------------------------------------------------
// §2.7 StaffRole — enum unico (D6) e ActorKind
// ---------------------------------------------------------------------------

/**
 * **8 valores distintos.** A conta "9 papeis" de D6 dupla-contava `DELIVERY` = `COURIER`;
 * `COURIER` e a grafia canonica (glossario do DOMAIN §1.2). `CUSTOMER`, `SYSTEM` e
 * `INTEGRATION` **nao** sao `StaffRole` — sao `ActorKind`.
 */
export const STAFF_ROLE_VALUES = STAFF_ROLES;

export const StaffRoleSchema = z.enum(STAFF_ROLE_VALUES);
export type StaffRole = z.infer<typeof StaffRoleSchema>;

export const STAFF_ROLE_MVP_VALUES = MVP_STAFF_ROLES;

/** Papeis com TOTP obrigatorio (CONTRACTS §5, item 6). */
export const STAFF_ROLE_TOTP_REQUIRED_VALUES = [
  'NETWORK_ADMIN',
  'INTEGRATION_ADMIN',
  'DPO',
  'PHARMACIST',
  'STORE_MANAGER',
] as const satisfies readonly StaffRole[];

/** `ActorKind` (DATA_MODEL, inalterado — CONTRACTS §2.7). */
export const ACTOR_KIND_VALUES = ACTOR_KINDS;

export const ActorKindSchema = z.enum(ACTOR_KIND_VALUES);
export type ActorKind = z.infer<typeof ActorKindSchema>;

// ---------------------------------------------------------------------------
// §2.8 PromotionType — mecanicas de promocao (X-33)
// ---------------------------------------------------------------------------

/**
 * 9 mecanicas, alinhadas ao DOMAIN §4.4. `PRECO_FIXO` substitui `PRECO_PROMOCIONAL`;
 * **`CUPOM` saiu do enum** — cupom e agregado proprio (`Coupon`), nao mecanica.
 * Mecanica desconhecida ou fora do MVP ⇒ **negada por padrao (fail-closed)**.
 */
export const PROMOTION_TYPE_VALUES = PROMOTION_TYPES;

export const PromotionTypeSchema = z.enum(PROMOTION_TYPE_VALUES);
export type PromotionType = z.infer<typeof PromotionTypeSchema>;

/** As **3** mecanicas do MVP (CONTRACTS §2.8). */
export const PROMOTION_TYPE_MVP_VALUES = MVP_OFFER_MECHANICS;

/**
 * Mecanicas com `CHECK` de banco que torna impossivel persisti-las para medicamento ou NBCAL
 * (REG-031, CONTRACTS §2.8).
 */
export const PROMOTION_TYPE_FORBIDDEN_FOR_MEDICATION_VALUES = [
  'LEVE_X_PAGUE_Y',
  'COMBO',
  'PROGRESSIVO',
  'BRINDE',
] as const satisfies readonly PromotionType[];

/**
 * `PriceSource` — o valor `ADMIN` esta **eliminado**: o backoffice nunca sobrescreve preco
 * unitario; desconto do app vive em `Promotion`, nunca em `salePriceCents` (ADR-0018).
 */
export const PRICE_SOURCE_VALUES = ['ERP', 'PWA'] as const;
export const PriceSourceSchema = z.enum(PRICE_SOURCE_VALUES);
export type PriceSource = z.infer<typeof PriceSourceSchema>;

// ---------------------------------------------------------------------------
// §2.9 ConsentPurpose e ConsentChannel
// ---------------------------------------------------------------------------

export const CONSENT_PURPOSE_VALUES = [
  'MARKETING_WHATSAPP', // M
  'MARKETING_PUSH', // M
  'MARKETING_EMAIL', // P — e-mail nao e canal do MVP
  'HEALTH_BUY_AGAIN_MEDICATION', // M — art. 11 LGPD
  'HEALTH_MEDICATION_REMINDER', // P — onda O3
  'HEALTH_LOYALTY_ON_MEDICATION', // P
  'PBM_DATA_SHARING', // P
  'DEPENDENT_DATA_PROCESSING', // P — Guardianship, onda O5
] as const;

export const ConsentPurposeSchema = z.enum(CONSENT_PURPOSE_VALUES);
export type ConsentPurpose = z.infer<typeof ConsentPurposeSchema>;

export const CONSENT_PURPOSE_MVP_VALUES = [
  'MARKETING_WHATSAPP',
  'MARKETING_PUSH',
  'HEALTH_BUY_AGAIN_MEDICATION',
] as const satisfies readonly ConsentPurpose[];

/** `BALCAO` e o canal do pedido assistido (D9). */
export const CONSENT_CHANNEL_VALUES = ['PWA', 'BALCAO', 'WHATSAPP', 'TELEFONE'] as const;
export const ConsentChannelSchema = z.enum(CONSENT_CHANNEL_VALUES);
export type ConsentChannel = z.infer<typeof ConsentChannelSchema>;

// ---------------------------------------------------------------------------
// §2.10 NotificationCategory
// ---------------------------------------------------------------------------

export const NOTIFICATION_CATEGORY_VALUES = [
  'PEDIDO', // M — utilitaria, sem nome de medicamento
  'ENTREGA', // M
  'PROMOCAO', // M — exige consentimento de marketing; nunca cita medicamento
  'SISTEMA', // M — OTP, seguranca, avisos operacionais
  'CUPOM', // P
  'RECOMPRA', // P
  'SAUDE', // P — exige HEALTH_MEDICATION_REMINDER
  'FIDELIDADE', // P
  'SERVICO', // P
] as const;

export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORY_VALUES);
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>;

export const NOTIFICATION_CATEGORY_MVP_VALUES = [
  'PEDIDO',
  'ENTREGA',
  'PROMOCAO',
  'SISTEMA',
] as const satisfies readonly NotificationCategory[];

// ---------------------------------------------------------------------------
// §2.11 PrescriptionStatus (agregado inteiro e P — onda O1)
// ---------------------------------------------------------------------------

/** 10 valores (DATA_MODEL). No MVP receita e foto anexada + conferencia presencial. */
export const PRESCRIPTION_STATUS_VALUES = [
  'RASCUNHO',
  'ENVIADA',
  'EM_ANALISE',
  'PENDENCIA',
  'APROVADA',
  'REPROVADA',
  'PARCIALMENTE_CONSUMIDA',
  'CONSUMIDA',
  'EXPIRADA',
  'REVOGADA',
] as const;

export const PrescriptionStatusSchema = z.enum(PRESCRIPTION_STATUS_VALUES);
export type PrescriptionStatus = z.infer<typeof PrescriptionStatusSchema>;

// ---------------------------------------------------------------------------
// §2.12 SubscriptionStatus (agregado inteiro e P)
// ---------------------------------------------------------------------------

/** 4 valores. Ciclo com medicamento RX exige receita `APROVADA` vigente. */
export const SUBSCRIPTION_STATUS_VALUES = [
  'ATIVA',
  'PAUSADA',
  'CANCELADA',
  'BLOQUEADA_RECEITA',
] as const;

export const SubscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUS_VALUES);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

// ---------------------------------------------------------------------------
// §2.13 AvailabilityBand — faixa de estoque exibivel
// ---------------------------------------------------------------------------

/**
 * Nome canonico `AvailabilityBand` (a "StockDisplayBand" da pauta de reconciliacao).
 * **O cliente NUNCA ve numero absoluto de estoque** (RN-063, X-22): resposta publica nao
 * contem `onHand` e "Restam N unidades" e proibido em qualquer superficie do cliente.
 */
export const AVAILABILITY_BAND_VALUES = [
  'IN_STOCK', // "Disponivel"
  'LOW_STOCK', // "Ultimas unidades" — sem numero; proibido como gatilho de escassez em medicamento
  'ON_REQUEST', // "Consultar" — tambem o estado degradado quando syncedAt > 45 min (isStale)
  'OUT_OF_STOCK', // "Indisponivel"
] as const;

export const AvailabilityBandSchema = z.enum(AVAILABILITY_BAND_VALUES);
export type AvailabilityBand = z.infer<typeof AvailabilityBandSchema>;

/** Rotulos de UI normativos (CONTRACTS §2.13). A UI nao inventa outro texto. */
export const AVAILABILITY_BAND_LABELS_PT_BR: Readonly<Record<AvailabilityBand, string>> = {
  IN_STOCK: 'Disponível',
  LOW_STOCK: 'Últimas unidades',
  ON_REQUEST: 'Consultar',
  OUT_OF_STOCK: 'Indisponível',
};

// ---------------------------------------------------------------------------
// §2.14 ConnectorCapability — o que um adaptador de ERP sabe fazer (D-1)
// ---------------------------------------------------------------------------

/**
 * **Um contrato so, e e este.** As tres grafias que circularam na virada de produto
 * (booleanos `readProducts/...`, uniao minuscula pontuada `product.read`/... e este
 * SCREAMING_SNAKE) sao a mesma coisa escrita de tres jeitos; vence o SCREAMING_SNAKE, que e a
 * convencao de enum do resto do projeto. As outras duas estao **eliminadas**.
 *
 * **Quem declara e o ADAPTADOR, nunca o perfil de instancia** (regra 1 da §2.14): capacidade
 * nao e campo de configuracao. O boot cruza o que a configuracao PEDE contra o que o adaptador
 * PUBLICA (regra 3, D-19) — a tabela normativa das 11 linhas mora em CONTRACTS §2.14, nao aqui.
 */
export const CONNECTOR_CAPABILITY_VALUES = [
  'CATALOG_READ',
  'PRICE_READ',
  'STOCK_READ',
  'CUSTOMER_READ',
  'FISCAL_READ',
  'SALE_WRITE',
  'CUSTOMER_WRITE',
  'DELTA_BY_TIMESTAMP',
  'SNAPSHOT_FULL',
  'PUSH_WEBHOOK',
] as const;

export const ConnectorCapabilitySchema = z.enum(CONNECTOR_CAPABILITY_VALUES);
export type ConnectorCapability = z.infer<typeof ConnectorCapabilitySchema>;

// ---------------------------------------------------------------------------
// §2.15 InstanceMode — modo de operacao da instancia (D-10)
// ---------------------------------------------------------------------------

/**
 * Um enum so, usado por `meta.mode` do perfil (IMPLANTACAO §2.3) **e** pelo campo `mode` do
 * `FleetHeartbeat` (FLEET §5.1). Nao existe um segundo vocabulario de modo.
 */
export const INSTANCE_MODE_VALUES = [
  'PRE_LANCAMENTO', // rotas de cliente: vitrine
  'PILOTO', // completas, restritas a rollout.pilotStoreCodes
  'PRODUCAO', // completas
  'RESTRITO', // vitrine — degradacao deliberada
  'MANUTENCAO', // indisponiveis, com pagina de manutencao
  'KILL_SWITCH', // indisponiveis (< 15 min, ADR-0025)
] as const;

export const InstanceModeSchema = z.enum(INSTANCE_MODE_VALUES);
export type InstanceMode = z.infer<typeof InstanceModeSchema>;

/**
 * "Vitrine" **nao e um modo** — e o comportamento de `PRE_LANCAMENTO` e `RESTRITO`
 * (CONTRACTS §2.15): catalogo publico visivel, zero coleta de dado pessoal, zero pedido,
 * zero consentimento, zero cadastro, botao "Pedir pelo WhatsApp". **Nada de 503.**
 */
export const INSTANCE_MODE_SHOWCASE_VALUES = [
  'PRE_LANCAMENTO',
  'RESTRITO',
] as const satisfies readonly InstanceMode[];

// ---------------------------------------------------------------------------
// Enums transversais nomeados em CONTRACTS §7.2
// ---------------------------------------------------------------------------

/**
 * Politica de default do ajuste de ruptura quando o cliente nao responde em 10 min (X-17).
 * Eliminados: `SEND_AVAILABLE`/`CANCEL_ORDER`, `ACCEPT_PARTIAL`/`REJECT` e `timeoutPolicy`
 * como nome do campo — o campo canonico e `OrderAdjustment.defaultOnTimeout`.
 */
export const ADJUSTMENT_TIMEOUT_DEFAULT_VALUES = ADJUSTMENT_TIMEOUT_DEFAULTS;
export const AdjustmentTimeoutDefaultSchema = z.enum(ADJUSTMENT_TIMEOUT_DEFAULT_VALUES);
export type AdjustmentTimeoutDefault = z.infer<typeof AdjustmentTimeoutDefaultSchema>;

/** Resposta do cliente ao ajuste de ruptura (API §6.12, `.../adjustment/respond`). */
export const ADJUSTMENT_RESPONSE_VALUES = [
  'ACCEPT_PARTIAL',
  'ACCEPT_SUBSTITUTE',
  'REJECT',
] as const;
export const AdjustmentResponseSchema = z.enum(ADJUSTMENT_RESPONSE_VALUES);
export type AdjustmentResponse = z.infer<typeof AdjustmentResponseSchema>;
